import React, { useState, useRef } from 'react';
import {
    X, Upload, FileText, AlertCircle, CheckCircle2,
    Download, Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import Button from './Button/Button';
import * as XLSX from 'xlsx';

const BulkUploadModal = ({ isOpen = true, onClose, onSuccess, config }) => {
    if (!isOpen) return null;
    const {
        title = 'Bulk Upload',
        subtitle = 'Import multiple records using an Excel file',
        templateEndpoint,
        validateEndpoint,
        uploadEndpoint,
        templateFilename = 'template.xlsx',
        dataKey = 'data',
        maxRecords = null,
        instructions = []
    } = config;

    const [file, setFile] = useState(null);
    const [isParsing, setIsParsing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [validationResults, setValidationResults] = useState(null);
    const [uploadResults, setUploadResults] = useState(null);
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    const [step, setStep] = useState(1);

    const fileInputRef = useRef(null);
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

    const getAuthHeaders = () => {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
                toast.error('Please select an Excel file (.xlsx or .xls)');
                return;
            }
            if (selectedFile.size > 5 * 1024 * 1024) {
                toast.error('File size should be less than 5MB');
                return;
            }
            setFile(selectedFile);
            handleExcelFile(selectedFile);
        }
    };

    const handleExcelFile = async (selectedFile) => {
        setIsParsing(true);
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                let result = { success: false };
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const json = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

                    if (json.length === 0) {
                        result = { success: false, error: 'Excel file is empty' };
                    } else {
                        const records = json.map(row => {
                            const trimmedRow = {};
                            for (const key in row) {
                                trimmedRow[key.trim()] = String(row[key]).trim();
                            }
                            return trimmedRow;
                        });
                        result = { success: true, records };
                    }

                    if (result.success) {
                        if (Number.isFinite(maxRecords) && maxRecords > 0 && result.records.length > maxRecords) {
                            toast.error(`Maximum ${maxRecords} records allowed per upload`);
                            setIsParsing(false);
                            return;
                        }
                        setParsedData(result.records);
                        setStep(2);
                        validateData(result.records);
                    } else {
                        toast.error(result.error || 'Failed to parse file');
                    }
                } catch (err) {
                    console.error(err);
                    toast.error('Failed to parse file content');
                }
                setIsParsing(false);
            };
            reader.onerror = () => {
                toast.error('Failed to read file');
                setIsParsing(false);
            };
            reader.readAsArrayBuffer(selectedFile);
        } catch (error) {
            toast.error('An error occurred while parsing the file');
            setIsParsing(false);
        }
    };

    const validateData = async (data) => {
        setIsValidating(true);
        try {
            const response = await fetch(`${API_BASE_URL}${validateEndpoint}`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ [dataKey]: data })
            });
            const result = await response.json();
            if (result.status === 'success' || result.responseCode === 200) {
                setValidationResults(result.data);
            } else {
                toast.error(result.message || result.responseMessage || 'Validation failed');
            }
        } catch (error) {
            toast.error('An error occurred during validation');
        } finally {
            setIsValidating(false);
        }
    };

    const handleUpload = async () => {
        if (!parsedData.length) return;
        setIsUploading(true);
        try {
            const response = await fetch(`${API_BASE_URL}${uploadEndpoint}`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ [dataKey]: parsedData, skipDuplicates })
            });
            const result = await response.json();
            if (response.ok && (result.status === 'success' || result.responseCode === 200 || result.responseCode === 201)) {
                setUploadResults(result.data);
                setStep(3);
                toast.success('Bulk upload completed');
            } else {
                toast.error(result.message || result.responseMessage || 'Upload failed');
            }
        } catch (error) {
            toast.error('An error occurred during upload');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}${templateEndpoint}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('token')}`
                }
            });
            if (!response.ok) throw new Error('Failed to download template');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            const now = new Date();
            const pad = (value) => String(value).padStart(2, '0');
            const timestamp = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}-${pad(now.getHours())}-${pad(now.getMinutes())}`;
            const dotIndex = templateFilename.lastIndexOf('.');
            const baseName = dotIndex > 0 ? templateFilename.slice(0, dotIndex) : templateFilename;
            const extension = dotIndex > 0 ? templateFilename.slice(dotIndex) : '.xlsx';
            a.href = url;
            a.download = `${baseName}-${timestamp}${extension}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Failed to download template');
        }
    };

    const reset = () => {
        setFile(null);
        setParsedData([]);
        setValidationResults(null);
        setUploadResults(null);
        setStep(1);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !mt-0">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white sticky top-0 z-10">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                        <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <X className="h-6 w-6 text-gray-400 hover:text-gray-600" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                    {step === 1 && (
                        <div className="max-w-3xl mx-auto space-y-8 py-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                                <div className="bg-white p-5 rounded-xl border border-blue-100 shadow-sm transition-all hover:shadow-md flex flex-col justify-between">
                                    <div>
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="p-2 bg-blue-50 rounded-lg">
                                                <Download className="h-5 w-5 text-[#0F2942]" />
                                            </div>
                                            <span className="font-semibold text-gray-900">1. Download Template</span>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                                            Get our template with the correct column structure for data.
                                        </p>
                                        {instructions && !Array.isArray(instructions) && instructions.download && instructions.download.length > 0 && (
                                            <ul className="text-xs text-gray-600 space-y-2 list-disc pl-4 mb-4 leading-relaxed">
                                                {instructions.download.map((inst, idx) => <li key={idx}>{inst}</li>)}
                                            </ul>
                                        )}
                                    </div>
                                    <Button variant="outline" size="sm" icon={<Download className="h-4 w-4" />} onClick={handleDownloadTemplate} className="w-full justify-center text-[#0F2942] border-blue-200 hover:bg-blue-50 mt-4">
                                        Download Template
                                    </Button>
                                </div>
                                <div className="bg-white p-5 rounded-xl border border-green-100 shadow-sm transition-all hover:shadow-md flex flex-col">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-2 bg-green-50 rounded-lg">
                                            <FileText className="h-5 w-5 text-green-600" />
                                        </div>
                                        <span className="font-semibold text-gray-900">2. Prepare Excel File</span>
                                    </div>
                                    {instructions ? (
                                        Array.isArray(instructions) ? (
                                            instructions.length > 0 ? (
                                                <ul className="text-xs text-gray-600 space-y-2 list-disc pl-4 leading-relaxed">
                                                    {instructions.map((inst, idx) => <li key={idx}>{inst}</li>)}
                                                </ul>
                                            ) : (
                                                <p className="text-sm text-gray-600 leading-relaxed">Open the template and fill in the required information.</p>
                                            )
                                        ) : (
                                            instructions.prepare && instructions.prepare.length > 0 ? (
                                                <ul className="text-xs text-gray-600 space-y-2 list-disc pl-4 leading-relaxed">
                                                    {instructions.prepare.map((inst, idx) => <li key={idx}>{inst}</li>)}
                                                </ul>
                                            ) : (
                                                <p className="text-sm text-gray-600 leading-relaxed">Open the template and fill in the required information.</p>
                                            )
                                        )
                                    ) : (
                                        <p className="text-sm text-gray-600 leading-relaxed">Open the template and fill in the required information.</p>
                                    )}
                                </div>
                            </div>
                            <div
                                className={`relative group border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-200 ${isParsing ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50/20'}`}
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onDrop={(e) => {
                                    e.preventDefault(); e.stopPropagation();
                                    const droppedFile = e.dataTransfer.files[0];
                                    if (droppedFile && droppedFile.name.match(/\.(xlsx|xls)$/i)) {
                                        setFile(droppedFile);
                                        handleExcelFile(droppedFile);
                                    } else {
                                        toast.error('Please drop an Excel file (.xlsx or .xls)');
                                    }
                                }}
                            >
                                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".xlsx, .xls" className="hidden" />
                                {isParsing ? (
                                    <div className="flex flex-col items-center">
                                        <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
                                        <p className="text-lg font-medium text-gray-900">Processing file...</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110">
                                            <Upload className="h-8 w-8 text-[#0F2942]" />
                                        </div>
                                        <h4 className="text-lg font-semibold text-gray-900 mb-2">Click to select or drag and drop Excel File</h4>
                                        <p className="text-sm text-gray-500 mb-6">Maximum file size: 5MB. Supported: .xlsx, .xls</p>
                                        <Button variant="primary" onClick={() => fileInputRef.current?.click()} className="bg-[#0F2942] hover:bg-[#0B2033] px-8">Select File</Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {step === 2 && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-center">
                                    <div className="text-2xl font-bold text-gray-900">{parsedData.length}</div>
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Rows</div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-center">
                                    <div className="text-2xl font-bold text-green-600">
                                        {isValidating ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (validationResults?.valid || validationResults?.validRows || 0)}
                                    </div>
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Ready to Upload</div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-center">
                                    <div className="text-2xl font-bold text-red-500">
                                        {isValidating ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (validationResults?.invalid || validationResults?.failed || 0)}
                                    </div>
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Errors Found</div>
                                </div>
                                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm text-center">
                                    <div className="text-2xl font-bold text-yellow-500">
                                        {isValidating ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (validationResults?.duplicateCount || validationResults?.duplicate || validationResults?.duplicates?.length || 0)}
                                    </div>
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Duplicates</div>
                                </div>
                            </div>

                            <div className="bg-white p-4 rounded-xl border border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-3 text-sm text-gray-700">
                                    <AlertCircle className="h-5 w-5 text-gray-400" />
                                    <span>How should we handle potential duplicate records?</span>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => setSkipDuplicates(true)} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${skipDuplicates ? 'bg-[#0F2942] text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Skip Duplicates</button>
                                    <button onClick={() => setSkipDuplicates(false)} className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${!skipDuplicates ? 'bg-[#0F2942] text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Keep All</button>
                                </div>
                            </div>

                            {validationResults && (validationResults.errors?.length > 0 || validationResults.duplicates?.length > 0) && (
                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                                        <span className="text-sm font-semibold text-gray-900">Validation Details</span>
                                    </div>
                                    <div className="max-h-[250px] overflow-y-auto p-4 space-y-3">
                                        {validationResults.errors?.map((err, idx) => (
                                            <div key={`err-${idx}`} className="flex gap-3 text-sm text-red-600 bg-red-50/50 p-2 rounded-lg">
                                                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                                <span>{typeof err === 'string' ? err : `Row ${err.row}: ${Array.isArray(err.errors) ? err.errors.join(', ') : err.errors}`}</span>
                                            </div>
                                        ))}
                                        {validationResults.duplicates?.map((dup, idx) => (
                                            <div key={`dup-${idx}`} className="flex gap-3 text-sm text-yellow-600 bg-yellow-50/50 p-2 rounded-lg">
                                                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                                <span>{typeof dup === 'string' ? dup : `Row ${dup.row}: ${dup.reason}`}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                                    <span className="text-sm font-semibold text-gray-900">Data Preview (First 5 Rows)</span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-gray-50/50">
                                                {parsedData.length > 0 && Object.keys(parsedData[0]).map(key => (
                                                    <th key={key} className="px-4 py-2.5 text-[11px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">{key}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {parsedData.slice(0, 5).map((row, i) => (
                                                <tr key={i} className="hover:bg-gray-50/30 transition-colors">
                                                    {Object.values(row).map((val, j) => (
                                                        <td key={j} className="px-4 py-2.5 text-xs text-gray-600 truncate max-w-[150px]">{val}</td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                    {step === 3 && (
                        <div className="text-center py-12 max-w-md mx-auto">
                            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                                <CheckCircle2 className="h-10 w-10 text-green-600" />
                            </div>
                            <h4 className="text-2xl font-bold text-gray-900 mb-2">Upload Completed!</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                                <div className="bg-green-50/50 p-3 rounded-xl border border-green-100 flex flex-col items-center">
                                    <div className="text-xl font-bold text-green-600">{uploadResults?.created || 0}</div>
                                    <div className="text-[10px] font-semibold text-green-700 uppercase tracking-widest">Created</div>
                                </div>
                                {uploadResults?.updated !== undefined && (
                                    <div className="bg-cyan-50/50 p-3 rounded-xl border border-cyan-100 flex flex-col items-center">
                                        <div className="text-xl font-bold text-cyan-600">{uploadResults?.updated || 0}</div>
                                        <div className="text-[10px] font-semibold text-cyan-700 uppercase tracking-widest">Updated</div>
                                    </div>
                                )}
                                <div className="bg-blue-50/50 p-3 rounded-xl border border-blue-100 flex flex-col items-center">
                                    <div className="text-xl font-bold text-[#0F2942]">{uploadResults?.skipped || 0}</div>
                                    <div className="text-[10px] font-semibold text-[#0B2033] uppercase tracking-widest">Skipped</div>
                                </div>
                                <div className="bg-red-50/50 p-3 rounded-xl border border-red-100 flex flex-col items-center">
                                    <div className="text-xl font-bold text-red-600">{uploadResults?.failed || 0}</div>
                                    <div className="text-[10px] font-semibold text-red-700 uppercase tracking-widest">Failed</div>
                                </div>
                            </div>

                            {uploadResults && (uploadResults.errors?.length > 0 || uploadResults.duplicates?.length > 0) && (
                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6 text-left">
                                    <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                                        <span className="text-xs font-semibold text-gray-900 uppercase">Processing Details & Warnings</span>
                                        <span className="text-[10px] font-medium text-gray-400 bg-white px-2 py-0.5 rounded border">
                                            {uploadResults.total} total rows
                                        </span>
                                    </div>
                                    <div className="max-h-[180px] overflow-y-auto p-3 space-y-2">
                                        {uploadResults.errors?.map((err, idx) => (
                                            <div key={`u-err-${idx}`} className="flex gap-2 text-[11px] text-red-600 bg-red-50/50 p-2 rounded-lg">
                                                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                                                <span>{typeof err === 'string' ? err : `Row ${err.row}: ${err.errors?.join(', ') || 'Processing error'}`}</span>
                                            </div>
                                        ))}
                                        {uploadResults.duplicates?.map((dup, idx) => (
                                            <div key={`u-dup-${idx}`} className="flex gap-2 text-[11px] text-yellow-600 bg-yellow-50/50 p-2 rounded-lg">
                                                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                                                <span>{typeof dup === 'string' ? dup : `Row ${dup.row}: ${dup.reason || 'Duplicate skipped'}`}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <Button variant="primary" onClick={() => { onSuccess && onSuccess(); onClose(); }} className="w-full bg-[#0F2942] hover:bg-[#0B2033] h-11 text-base shadow-lg transition-transform hover:scale-[1.02]">
                                Return to List
                            </Button>
                        </div>
                    )}
                </div>

                {step !== 3 && (
                    <div className="px-6 py-4 border-t border-gray-100 bg-white flex justify-between items-center gap-4">
                        <Button variant="outline" onClick={step === 2 ? reset : onClose} disabled={isUploading || isValidating || isParsing} className="px-6">
                            {step === 2 ? 'Upload Another' : 'Cancel'}
                        </Button>
                        {step === 2 && (
                            <div className="flex gap-3">
                                <Button
                                    variant="primary" onClick={handleUpload}
                                    disabled={isUploading || isValidating || ((validationResults?.valid || validationResults?.validRows || 0) === 0 && !validationResults?.duplicate && !validationResults?.duplicates?.length)}
                                    className="bg-[#0F2942] hover:bg-[#0B2033] px-10 min-w-32"
                                >
                                    {isUploading ? (
                                        <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /><span>Uploading...</span></div>
                                    ) : 'Start Upload'}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkUploadModal;
